import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, fmtMoney, fmtNum } from "@/lib/api";
import { PageHeader, Card, StatusBadge, Field, TextInput, CrudActions } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, UserPlus, Download } from "lucide-react";
import { downloadPartyLedgerCsv, downloadPartyStockLotCsv, downloadPartyStockStackCsv } from "@/lib/downloadCsv";

const ROLE_OPTIONS = ["Stock Owner", "Sender", "Receiver", "Buyer", "Seller", "Transporter", "Vehicle Owner", "Labour Contractor", "Warehouse Owner", "Other"];

export default function Parties() {
  const qc = useQueryClient();
  const [selId, setSelId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: parties = [], isLoading } = useQuery({
    queryKey: ["masters", "parties"],
    queryFn: async () => (await api.get("/masters/parties")).data,
  });
  const { data: detail } = useQuery({
    queryKey: ["party-view", selId],
    queryFn: async () => (await api.get(`/reports/parties/${selId}`)).data,
    enabled: !!selId,
  });

  return (
    <div data-testid="parties-page">
      <PageHeader title="Parties & Ledger" subtitle="Universal Party Master — one party, many roles"
        action={
          <button data-testid="add-party-btn" onClick={() => setAddOpen(true)}
            className="flex h-11 items-center gap-2 rounded-full bg-emerald-800 px-5 font-semibold text-white hover:bg-emerald-900 transition-colors">
            <UserPlus size={18} /> Add Party
          </button>
        }
      />
      {isLoading ? (
        <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-emerald-700" size={32} /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {parties.map((p) => (
            <Card
              key={p.id}
              data-testid={`party-card-${p.id}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelId(p.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelId(p.id);
                }
              }}
              className="cursor-pointer p-4 transition-transform hover:scale-[1.01] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 text-left">
                  <div className="font-display font-bold text-stone-900">{p.name}</div>
                  <div className="text-xs text-stone-400 font-mono">{p.code}</div>
                </div>
                {p.active === false ? <StatusBadge tone="neutral">Inactive</StatusBadge> : (
                  <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <CrudActions
                      testid={`party-${p.id}`}
                      onEdit={() => { setEditing(p); setAddOpen(true); }}
                      onDelete={async () => {
                        if (!window.confirm(`Deactivate party ${p.name}?`)) return;
                        try { await api.delete(`/masters/parties/${p.id}`); toast.success("Party deactivated"); qc.invalidateQueries({ queryKey: ["masters", "parties"] }); }
                        catch (e) { toast.error(apiError(e)); }
                      }}
                    />
                  </div>
                )}
              </div>
              {p.active === false && (
                <div className="mt-2" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <CrudActions inactive onRestore={async () => {
                    try { await api.put(`/masters/parties/${p.id}`, { active: true }); toast.success("Party restored"); qc.invalidateQueries({ queryKey: ["masters", "parties"] }); }
                    catch (e) { toast.error(apiError(e)); }
                  }} />
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(p.roles || []).map((r) => <StatusBadge key={r} tone="emerald">{r}</StatusBadge>)}
              </div>
              {p.mobile && <div className="mt-3 text-xs text-stone-500">{p.mobile}</div>}
            </Card>
          ))}
        </div>
      )}

      <AddPartyDialog open={addOpen} record={editing} onClose={() => { setAddOpen(false); setEditing(null); }} onSaved={() => { qc.invalidateQueries({ queryKey: ["masters", "parties"] }); }} />

      <Dialog open={!!selId} onOpenChange={(o) => !o && setSelId(null)}>
        <DialogContent className="max-w-3xl" data-testid="party-view-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  {detail.party.name}
                  <span className="text-sm font-normal text-stone-400 font-mono">{detail.party.code}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-wrap gap-1.5">
                {(detail.party.roles || []).map((r) => <StatusBadge key={r} tone="emerald">{r}</StatusBadge>)}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Info label="Closing Balance" value={fmtMoney(detail.closing_balance)} big />
                <Info label="As Owner" value={detail.role_history.stock_owner} />
                <Info label="As Sender/Receiver" value={detail.role_history.sender + detail.role_history.receiver} />
              </div>

              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400">
                    Current Stock ({detail.stock.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid="party-stock-lot-download"
                      disabled={!detail.stock?.length}
                      onClick={() => {
                        downloadPartyStockLotCsv(detail);
                        toast.success("Lot-wise stock downloaded");
                      }}
                      className="flex h-8 items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
                    >
                      <Download size={14} /> Lot wise
                    </button>
                    <button
                      type="button"
                      data-testid="party-stock-stack-download"
                      disabled={!detail.stock?.length}
                      onClick={() => {
                        downloadPartyStockStackCsv(detail);
                        toast.success("Stack-wise stock downloaded");
                      }}
                      className="flex h-8 items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
                    >
                      <Download size={14} /> Stack wise
                    </button>
                  </div>
                </div>
                {detail.stock.length === 0 ? (
                  <div className="text-sm text-stone-400">No stock held.</div>
                ) : (
                  <div className="space-y-1.5">
                    {detail.stock.map((s, i) => (
                      <div key={i} className="flex justify-between gap-2 rounded-lg bg-stone-50 px-3 py-2 text-sm">
                        <span className="font-mono text-xs text-emerald-700 shrink-0">{s.lot_number}</span>
                        <span className="min-w-0 truncate text-stone-700">{s.item} · {s.warehouse}/{s.stack}</span>
                        <span className="font-semibold shrink-0">{fmtNum(s.bags)} bags</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400">Financial Ledger</div>
                  <button
                    type="button"
                    data-testid="party-ledger-download"
                    disabled={!detail.ledger?.length}
                    onClick={() => {
                      downloadPartyLedgerCsv(detail);
                      toast.success("Ledger downloaded");
                    }}
                    className="flex h-8 items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
                  >
                    <Download size={14} /> Download CSV
                  </button>
                </div>
                <div className="overflow-x-auto rounded-lg border border-stone-100">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
                      <tr>
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2">Description</th>
                        <th className="px-3 py-2">Ref</th>
                        <th className="px-3 py-2 text-right">Debit</th>
                        <th className="px-3 py-2 text-right">Credit</th>
                        <th className="px-3 py-2 text-right">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.ledger.length === 0 && (
                        <tr><td colSpan={7} className="px-3 py-4 text-center text-stone-400">No ledger entries.</td></tr>
                      )}
                      {detail.ledger.map((e) => (
                        <tr key={e.id} className="border-t border-stone-100">
                          <td className="px-3 py-2 whitespace-nowrap">{e.date ? String(e.date).slice(0, 10) : "—"}</td>
                          <td className="px-3 py-2">{e.entry_type}</td>
                          <td className="px-3 py-2 text-stone-600">{e.description}</td>
                          <td className="px-3 py-2 font-mono text-xs">{e.source_txn_number}</td>
                          <td className="px-3 py-2 text-right text-red-600">{e.debit ? fmtMoney(e.debit) : "—"}</td>
                          <td className="px-3 py-2 text-right text-emerald-700">{e.credit ? fmtMoney(e.credit) : "—"}</td>
                          <td className="px-3 py-2 text-right font-semibold">{fmtMoney(e.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddPartyDialog({ open, onClose, onSaved, record }) {
  const blank = { code: "", name: "", mobile: "", gstin: "", email: "", contact_person: "", address: "", pan: "", roles: [] };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  const toggle = (r) => setF((s) => ({ ...s, roles: s.roles.includes(r) ? s.roles.filter((x) => x !== r) : [...s.roles, r] }));

  useEffect(() => {
    if (!open) return;
    if (record) {
      setF({
        code: record.code || "", name: record.name || "", mobile: record.mobile || "",
        gstin: record.gstin || "", email: record.email || "", contact_person: record.contact_person || "",
        address: record.address || "", pan: record.pan || "", roles: record.roles || [],
      });
    } else setF(blank);
  }, [open, record]);

  const save = async () => {
    if (!f.code || !f.name) return toast.error("Code and name are required");
    setSaving(true);
    try {
      if (record) {
        await api.put(`/masters/parties/${record.id}`, f);
        toast.success("Party updated");
      } else {
        await api.post("/masters/parties", f);
        toast.success("Party added");
      }
      onSaved();
      setF(blank);
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-party-dialog">
        <DialogHeader><DialogTitle className="font-display">{record ? "Edit Party" : "Add Party"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" required><TextInput value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="party-code" /></Field>
          <Field label="Name" required><TextInput value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="party-name" /></Field>
          <Field label="Contact person"><TextInput value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} /></Field>
          <Field label="Mobile"><TextInput value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></Field>
          <Field label="Email"><TextInput value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="GSTIN"><TextInput value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value })} /></Field>
          <Field label="PAN"><TextInput value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value })} /></Field>
          <Field label="Address"><TextInput value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-500 mb-2">Roles</div>
          <div className="flex flex-wrap gap-2">
            {ROLE_OPTIONS.map((r) => (
              <button key={r} type="button" onClick={() => toggle(r)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${f.roles.includes(r) ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-600 border-stone-200 hover:border-emerald-400"}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-party-btn"
          className="mt-2 flex h-11 items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} {record ? "Update Party" : "Save Party"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ label, value, big }) => (
  <div className="rounded-lg bg-stone-50 p-3">
    <div className="text-[11px] text-stone-500">{label}</div>
    <div className={`font-semibold text-stone-900 ${big ? "text-lg" : ""}`}>{value}</div>
  </div>
);
